// src/services/organizationService.js

import { Api } from "../Api";

// Organization switching service for Ethosphere users
export class OrganizationService {
  constructor() {
    this.organizationOverride = null;
    this.availableOrganizations = null;
    this.canSwitchOrganizations = false;
  }

  // Get user's available organizations
  async getAvailableOrganizations() {
    if (this.availableOrganizations !== null) {
      return {
        organizations: this.availableOrganizations,
        canSwitch: this.canSwitchOrganizations,
      };
    }

    try {
      const response = await Api.get("/user/organizations");
      this.availableOrganizations = response.data.organizations;
      this.canSwitchOrganizations = response.data.can_switch_organizations;

      return {
        organizations: this.availableOrganizations,
        canSwitch: this.canSwitchOrganizations,
      };
    } catch (error) {
      console.error("Error fetching available organizations:", error);
      return {
        organizations: [],
        canSwitch: false,
      };
    }
  }

  // Switch to a different organization (Ethosphere users only)
  async switchOrganization(organizationId) {
    try {
      const response = await Api.post("/user/switch-organization", {
        organization_id: organizationId,
      });

      if (response.data.success) {
        // Set local override
        this.organizationOverride = response.data.active_organization;

        // Update available organizations to reflect active state
        if (this.availableOrganizations) {
          this.availableOrganizations = this.availableOrganizations.map(
            (org) => ({
              ...org,
              is_active: org.id === organizationId,
            })
          );
        }

        return response.data.active_organization;
      }
      throw new Error("Organization switch failed");
    } catch (error) {
      console.error("Error switching organization:", error);
      throw error;
    }
  }

  // Get the currently active organization
  getActiveOrganization() {
    if (this.organizationOverride) {
      return this.organizationOverride;
    }

    if (this.availableOrganizations) {
      return this.availableOrganizations.find((org) => org.is_active);
    }

    return null;
  }

  // Get effective organization name for analysis calls
  getEffectiveOrganizationName() {
    const activeOrg = this.getActiveOrganization();
    return activeOrg ? activeOrg.name.toLowerCase() : null;
  }

  // Check if user can switch organizations
  async canSwitch() {
    const { canSwitch } = await this.getAvailableOrganizations();
    return canSwitch;
  }

  // Clear organization cache (useful for logout)
  clearCache() {
    this.organizationOverride = null;
    this.availableOrganizations = null;
    this.canSwitchOrganizations = false;
  }

  // Get organization for display purposes
  getOrganizationDisplayName() {
    const activeOrg = this.getActiveOrganization();
    return activeOrg ? activeOrg.name : null;
  }

  // Read an organization's per-org role/rubric map (organizations.role_config).
  // Admin-only; a non-platform admin may only read their own org. Returns
  // { organization_id, role_config } (role_config may be null when unset).
  async getOrgRoleConfig(organizationId) {
    try {
      const response = await Api.get(
        `/organizations/${organizationId}/role-config`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching organization role config:", error);
      throw error;
    }
  }

  // Set an organization's per-org role/rubric map (organizations.role_config).
  // Admin-only; a non-platform admin may only configure their own org. Pass
  // null to clear (org falls back to the notetaking-safe default).
  async setOrgRoleConfig(organizationId, roleConfig) {
    try {
      const response = await Api.put(
        `/organizations/${organizationId}/role-config`,
        { role_config: roleConfig }
      );
      return response.data;
    } catch (error) {
      console.error("Error saving organization role config:", error);
      throw error;
    }
  }
}

// Create singleton instance
export const organizationService = new OrganizationService();

// Export convenience functions
export const getAvailableOrganizations = () =>
  organizationService.getAvailableOrganizations();
export const switchOrganization = (orgId) =>
  organizationService.switchOrganization(orgId);
export const getActiveOrganization = () =>
  organizationService.getActiveOrganization();
export const getEffectiveOrganizationName = () =>
  organizationService.getEffectiveOrganizationName();
export const canSwitchOrganizations = () => organizationService.canSwitch();
export const clearOrganizationCache = () => organizationService.clearCache();
export const getOrganizationDisplayName = () =>
  organizationService.getOrganizationDisplayName();
export const getOrgRoleConfig = (orgId) =>
  organizationService.getOrgRoleConfig(orgId);
export const setOrgRoleConfig = (orgId, roleConfig) =>
  organizationService.setOrgRoleConfig(orgId, roleConfig);
